import React, { useState, useMemo, useRef, useEffect } from 'react';
import { TripSuggestionStyles, StyledButtons } from '../survey-helpers/trip-suggestion/trip-suggestion-styles';
import { doc, runTransaction } from "firebase/firestore";
import { db } from '../../firebase';

const DisplayResults = ({ rankings, DestinationRankings, handleViewResults, showSignUpModal, user, docId }) => {
    const userId = user?.uid || "Other";
    const [rated, setRated] = useState({});

    // Filters
    const [uniqueCountries, setUniqueCountries] = useState(true);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside of it
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Extract all countries to populate filter dropdown
    const availableCountries = useMemo(() => {
      const countries = rankings.map(r => r.country).filter(Boolean);
      const uniqueCountries = [...new Set(countries)];
      return uniqueCountries.sort((a, b) => a.localeCompare(b));
    }, [rankings]);

    // State handlers
    const handleUniqueToggle = (e) => {
      const isChecked = e.target.checked;
      setUniqueCountries(isChecked);
      if (isChecked) {
        setSelectedCountries([]); // Clear selected countries when unique is turned on
      }
    };

    const handleCountryToggle = (country) => {
      setSelectedCountries(prev => {
        let updated;
        if (prev.includes(country)) {
          updated = prev.filter(c => c !== country); // Remove if already selected
        } else {
          updated = [...prev, country]; // Add if not selected
        }

        // Turn off unique if countries not empty
        if (updated.length > 0) {
          setUniqueCountries(false);
        } else {
          // Turn unique back on when none selected
          setUniqueCountries(true);
        }
        return updated;
      });
    };

    const visibleRankings = useMemo(() => {
      let filtered = [...rankings];

      // Filter by country
      if (selectedCountries.length > 0) {
        filtered = filtered.filter(item => selectedCountries.includes(item.country));
      }

      // Unique filter: 1 destination per country
      if (uniqueCountries) {
        const seen = new Set();
        filtered = filtered.filter(item => {
          if (seen.has(item.country)) return false;
          seen.add(item.country);
          return true;
        });
      }

      // Return top 8
      return filtered.slice(0, 8);
    }, [rankings, uniqueCountries, selectedCountries]);

    // Determine what text to show on the closed dropdown button
    const dropdownButtonText = useMemo(() => {
      if (selectedCountries.length === 0) return "All Countries";
      if (selectedCountries.length === 1) return selectedCountries[0];
      return `${selectedCountries.length} Countries Selected`;
    }, [selectedCountries]);

    // Populate feedback to Firestore
    const handleRating = async (index, rating) => {
      if (!docId) {
        console.warn('No docId provided for feedback write');
        return;
      }
      const resultDoc = doc(db, "Results", userId, "Game", docId);

      try {
        await runTransaction(db, async (transaction) => {
          const snap = await transaction.get(resultDoc);
          const data = snap.exists() ? snap.data() : {};
          const feedback = Array.isArray(data.feedback) ? [...data.feedback] : [];
          while (feedback.length <= index) feedback.push(null);
          feedback[index] = rating;
          transaction.update(resultDoc, { feedback });
        });
        // mark as rated locally so UI collapses
        setRated(prev => ({ ...prev, [index]: true }));
      } catch (err) {
        console.error('Failed to write feedback:', err);
      }
    };
  
    return (
      <TripSuggestionStyles>
        <div className="header">
          <div className="title-container">
            <h1 className="your-preferences">Your Suggestions:</h1>
          </div>
          <div className="description-container">
            <button onClick={() => handleViewResults()} className='header-button'>
              Continue Selecting
            </button>
            {!user ? (
              <button onClick={() => showSignUpModal(true)} className='header-button'>
                Save Results
              </button>
            ) : (
              <></>
            )}
          </div>
        </div>

        {/* --- CUSTOM MULTI-SELECT DROPDOWN FILTER PANEL --- */}
        <div className="filter-panel mb-4 p-3" style={{ background: '#f8f9fa', borderRadius: '8px' }}>
          <div className="d-flex flex-wrap gap-4 align-items-center">
            
            {/* Custom Multi-Select Dropdown */}
            <div className="d-flex flex-column position-relative" ref={dropdownRef} style={{ width: '250px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>Filter by Country</label>
              
              {/* Trigger Button */}
              <button 
                type="button"
                className="form-select text-start d-flex justify-content-between align-items-center"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{ 
                  background: '#fff', 
                  border: '1px solid #ced4da', 
                  borderRadius: '4px',
                  padding: '6px 12px',
                  cursor: 'pointer'
                }}
              >
                <span>{dropdownButtonText}</span>
              </button>

              {/* Floating Dropdown Menu */}
              {dropdownOpen && (
                <div className="position-absolute w-100 shadow-sm" style={{ 
                  top: '100%', 
                  left: 0, 
                  zIndex: 1000, 
                  background: '#fff', 
                  border: '1px solid #ced4da', 
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  marginTop: '4px'
                }}>
                  {/* --- NEW: Dedicated "All" Option --- */}
                  <div 
                    onClick={() => {
                      setSelectedCountries([]);
                      setUniqueCountries(true);
                      setDropdownOpen(false); // Close menu on selection
                    }}
                    className="dropdown-item d-flex align-items-center justify-content-between px-3 py-2"
                    style={{ 
                      cursor: 'pointer',
                      background: selectedCountries.length === 0 ? '#f1f3f5' : 'transparent',
                      fontSize: '14px',
                      fontWeight: selectedCountries.length === 0 ? '600' : '400',
                      userSelect: 'none',
                      borderBottom: '1px solid #e9ecef' // Visual separator
                    }}
                  >
                    <span>All Countries</span>
                    {selectedCountries.length === 0 && <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>✓</span>}
                  </div>
                  {availableCountries.map(country => {
                    const isSelected = selectedCountries.includes(country);
                    return (
                      <div 
                        key={country}
                        onClick={() => handleCountryToggle(country)}
                        className="dropdown-item d-flex align-items-center justify-content-between px-3 py-2"
                        style={{ 
                          cursor: 'pointer',
                          background: isSelected ? '#f1f3f5' : 'transparent',
                          fontSize: '14px',
                          userSelect: 'none'
                        }}
                      >
                        <span>{country}</span>
                        {isSelected && <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>✓</span>}
                      </div>
                    );
                  })}
                  {selectedCountries.length > 0 && (
                    <div 
                      onClick={() => {
                        setSelectedCountries([]);
                        setUniqueCountries(true);
                        setDropdownOpen(false);
                      }}
                      className="dropdown-item text-danger text-center border-top py-2"
                      style={{ cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                      Clear Selections
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Unique Switch Toggle */}
            <div className="form-check form-switch mt-3">
              <input 
                className="form-check-input" 
                type="checkbox" 
                id="uniqueCountrySwitch" 
                checked={uniqueCountries}
                onChange={handleUniqueToggle}
                style={{ cursor: 'pointer' }}
              />
              <label className="form-check-label" htmlFor="uniqueCountrySwitch" style={{ cursor: 'pointer', marginLeft: -10, marginTop: 1 }}>
                Show 1 destination per country
              </label>
            </div>

          </div>
        </div>

        <div>
          {visibleRankings.map((ranking, index) => {
          
            return (
              <div key={index} className="row gy-3 box p-3 mb-5">
                <div className="col-12 d-flex flex-row justify-content-between align-items-center mt-0">
                  <div className='left-container'>
                    <span className="package-description text-center text-md-start">
                      {index + 1}. {ranking.name}
                      <span 
                        className="badge bg-success rounded-pill" 
                        style={{ 
                          fontSize: '0.65em',
                          padding: '0.3em 0.7em',
                          marginLeft: '1.5em',
                          alignSelf: 'center',
                          transform: 'translateY(-5px)',
                          letterSpacing: '0.03em',
                          fontWeight: '600'
                        }}
                      >
                        {ranking.matchStrength}% Match
                      </span>
                    </span>
                    <div className='button-container'>
                      <div className={`feedback-buttons ${rated[index] ? 'hidden' : 'visible'}`}>
                        <p className='description'>Do you like this suggestion?</p>
                        <StyledButtons
                          className="dislike"
                          onClick={() => handleRating(index, -1)}
                        ></StyledButtons>
                        <StyledButtons
                          className="like"
                          onClick={() => handleRating(index, 1)}
                        ></StyledButtons>
                      </div>
                      <p className={`description thank-you ${rated[index] ? 'visible' : ''}`}>Thank you for your feedback.</p>
                    </div>
                  </div>
                  
                  {/* <img className="img-fluid" src={destination.image} alt={ranking.city} /> */}
                </div>
              </div>
            );
          })}
        </div>
      </TripSuggestionStyles>
    );
  };
  
  export default DisplayResults;